import { applySnapshot, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { IDtoLoginInfo } from '~/types/apis/auth/post.signin';
import { getBase64 } from '~/utils/commonUtils';
import { sendUserId, sendUserProperties } from '~/utils/analytics';

const IDENTIFIER = 'authModel';

/**
 * 로그인 사용자 정보 모델
 */
const model = types
  .model(IDENTIFIER, {
    /** 스토어 아이덴티티 */
    identifier: types.optional(types.identifier, IDENTIFIER),
    idAdminDealer: types.number,
    idCarBrand: types.maybeNull(types.number),
    count: types.maybeNull(types.number),
    isAffiliatedAccount: types.maybeNull(types.string),
    dealerType: types.maybeNull(types.number),
    originDealerType: types.maybeNull(types.number),
    name: types.maybeNull(types.string),
    nickName: types.maybeNull(types.string),
    nameCardPhone: types.maybeNull(types.string),
    isNameCardDown: types.maybeNull(types.string),
    isSubtraction: types.maybeNull(types.string),
    importedYn: types.maybeNull(types.string),
    expDate: types.maybeNull(types.number),
    brand: types.maybeNull(types.string),
    brand_logo_url: types.maybeNull(types.string),
    product: types.maybeNull(types.string),
    isFirst: types.maybeNull(types.string),
    dealerCompany: types.maybeNull(types.string),
    productName: types.maybeNull(types.string),
    base_name: types.maybeNull(types.string),
    idShowRoom: types.maybeNull(types.number),
    isInService: types.maybeNull(types.boolean),
    isDomesticBrand: types.maybeNull(types.boolean),
  })
  .actions((self) => ({
    /** 초기화 */
    setInit() {
      applySnapshot(self, defaultValue);
    },
    /* 로그인 정보 스토어에 저장 */
    setLoginInfo() {
      const uif = localStorage?.getItem('uif') || '';

      if (uif) {
        const loginInfo: IDtoLoginInfo = JSON.parse(
          getBase64(localStorage?.getItem('uif') || ''),
        );

        /* 애널리틱스 설정 */
        sendUserId(loginInfo?.id_admin_dealer?.toString());
        sendUserProperties({
          brand: loginInfo?.brand,
        });
        sendUserProperties({
          dealer_type: loginInfo?.dealer_type?.toString(),
        });

        if (loginInfo.showroom_id) {
          sendUserProperties({
            showroom_id: loginInfo?.showroom_id?.toString(),
          });
        }

        self.idAdminDealer = loginInfo.id_admin_dealer;
        self.idCarBrand = loginInfo.id_carbrand;
        self.count = loginInfo.count;
        self.isAffiliatedAccount = loginInfo.is_affiliated_account;
        self.dealerType = loginInfo.dealer_type;
        self.originDealerType = loginInfo.origin_dealer_type;
        self.name = loginInfo.name;
        self.nickName = loginInfo.nick_name;
        self.nameCardPhone = loginInfo.name_card_phone;
        self.isNameCardDown = loginInfo.is_name_card_down;
        self.isSubtraction = loginInfo.is_subtraction;
        self.importedYn = loginInfo.imported_yn;
        self.expDate = loginInfo.exp_date;
        self.brand = loginInfo.brand;
        self.brand_logo_url = loginInfo.brand_logo_url;
        self.product = loginInfo.product;
        self.isFirst = loginInfo.is_first;
        self.dealerCompany = loginInfo.dealer_company;
        self.productName = loginInfo.product_name;
        self.base_name = loginInfo.base_name;
        self.idShowRoom = loginInfo.showroom_id;
        self.isInService = [1, 2, 24].includes(loginInfo.dealer_type);
        //self.isDomesticBrand = loginInfo.id_carbrand in [2, 8, 16, 18, 23, 36]; //기아, 르노코리아, 쉐보레, KG(쌍용), 제네시스, 현대
        self.isDomesticBrand = [
          '기아',
          '르노코리아',
          '쉐보레',
          'KG 모빌리티',
          '제네시스',
          '현대',
        ].includes(loginInfo.brand);
        // TODO: 추후 id 값으로 변경 필요
      }
    },
    /* dealer type 스토어에 저장 */
    setDealerType(dealer_type: number, origin_dealer_type: number) {
      self.dealerType = dealer_type;
      self.originDealerType = origin_dealer_type;
      self.isInService = [1, 2, 24].includes(dealer_type); /* 1, 2, 24 이 아니면 서비스 이용중 아님 */
    },

    /* id_carbrand 값 스토어에 저장 */
    setIdCarBrand(id_carbrand: number, brand: string) {
      self.idCarBrand = id_carbrand;
      self.isDomesticBrand = [
        '기아',
        '르노코리아',
        '쉐보레',
        'KG 모빌리티',
        '제네시스',
        '현대',
      ].includes(brand);
      //self.isDomesticBrand = id_carbrand in [2, 8, 16, 18, 23, 36]; //기아, 르노코리아, 쉐보레, KG(쌍용), 제네시스, 현대
    },
  }))
  .views((self) => ({
    getLoginInfo() {
      if (self.idAdminDealer) {
        self.setLoginInfo();
      }

      return self;
    },
  }));

/** 초기화 값 */
const defaultValue: SnapshotIn<typeof model> = {
  identifier: IDENTIFIER,
  idAdminDealer: 0,
  idCarBrand: 0,
  count: null,
  isAffiliatedAccount: null,
  dealerType: null,
  originDealerType: null,
  name: null,
  nickName: null,
  nameCardPhone: null,
  isNameCardDown: null,
  isSubtraction: null,
  importedYn: null,
  expDate: null,
  brand: null,
  brand_logo_url: null,
  product: null,
  isFirst: null,
  dealerCompany: null,
  productName: null,
  base_name: null,
  idShowRoom: null,
  isInService: null,
  isDomesticBrand: null,
};

/** create or initialize */
const create = model.create(defaultValue);

/** store */
const store = {
  create,
  defaultValue,
  model,
};

/** 모델 타입 */
export type IAuthModelType = Instance<typeof model>;

export default store;
